export const CUSTOM_RULE_INSTRUCTION_FILE_PATHS_MAX_COUNT = 10;
export const CUSTOM_RULE_INSTRUCTION_FILE_PATH_MAX_LENGTH = 500;

const GLOB_CHARACTERS_REGEX = /[*?[\]{}]/;

export type InstructionFilePathValidationIssue = {
  index?: number;
  path?: string;
  message: string;
};

export class InstructionFilePathValidationError extends Error {
  public issues: InstructionFilePathValidationIssue[];

  constructor(issues: InstructionFilePathValidationIssue[]) {
    super(issues.map((issue) => issue.message).join('; '));
    this.name = 'InstructionFilePathValidationError';
    this.issues = issues;
  }
}

export function validateInstructionFilePaths(filePaths: string[]): {
  normalizedPaths: string[];
  issues: InstructionFilePathValidationIssue[];
} {
  const issues: InstructionFilePathValidationIssue[] = [];
  const normalizedPaths: string[] = [];
  const seenPaths = new Set<string>();

  if (filePaths.length > CUSTOM_RULE_INSTRUCTION_FILE_PATHS_MAX_COUNT) {
    issues.push({
      message: `Use ${CUSTOM_RULE_INSTRUCTION_FILE_PATHS_MAX_COUNT} or fewer instruction files`,
    });
  }

  filePaths.forEach((rawPath, index) => {
    const path = rawPath.trim().replaceAll('\\', '/');

    if (!path) {
      issues.push({ index, message: 'Instruction file paths cannot be empty' });
      return;
    }

    if (path.length > CUSTOM_RULE_INSTRUCTION_FILE_PATH_MAX_LENGTH) {
      issues.push({
        index,
        path,
        message: `Instruction file paths must be ${CUSTOM_RULE_INSTRUCTION_FILE_PATH_MAX_LENGTH} characters or fewer`,
      });
      return;
    }

    if (path.startsWith('/') || /^[A-Z]:\//i.test(path)) {
      issues.push({ index, path, message: 'Instruction file paths must be repo-relative' });
      return;
    }

    if (path.includes('://')) {
      issues.push({ index, path, message: 'Instruction file paths cannot be URLs' });
      return;
    }

    if (path.endsWith('/')) {
      issues.push({
        index,
        path,
        message: 'Instruction file paths must point to files, not directories',
      });
      return;
    }

    if (GLOB_CHARACTERS_REGEX.test(path)) {
      issues.push({
        index,
        path,
        message: 'Instruction file paths cannot contain glob characters',
      });
      return;
    }

    const segments = path.split('/');
    if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
      issues.push({
        index,
        path,
        message: 'Instruction file paths cannot contain empty, ".", or ".." segments',
      });
      return;
    }

    if (seenPaths.has(path)) {
      issues.push({ index, path, message: 'Instruction file paths cannot be duplicated' });
      return;
    }

    seenPaths.add(path);
    normalizedPaths.push(path);
  });

  if (normalizedPaths.length === 0 && issues.length === 0) {
    issues.push({ message: 'At least one instruction file path is required' });
  }

  return { normalizedPaths, issues };
}

export function normalizeInstructionFilePaths(filePaths: string[]): string[] {
  const { normalizedPaths, issues } = validateInstructionFilePaths(filePaths);

  if (issues.length > 0) {
    throw new InstructionFilePathValidationError(issues);
  }

  return normalizedPaths;
}
